import React, { useMemo } from 'react';

import { Box } from '@adsk/alloy-react-box';
import Dropdown, {
  Menu,
  MenuItem,
  isGroupHeader,
  MenuGroupHeader,
  defaultLabelGetter,
} from '@adsk/alloy-react-dropdown';
import { ClockArrowCircleIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import theme from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import {
  DropdownRenderMenuType,
  DropdownSingleSelectProps,
  Schedule,
} from '../../types';

const RecentlySeen = () => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));

  return (
    <Box
      display="grid"
      gridAutoFlow="column"
      alignItems="center"
      justifyContent="flex-start"
      color="charcoal500"
      fontSize="14px"
      style={{
        columnGap: '3px',
      }}
    >
      <ClockArrowCircleIcon size={16} color={theme.colors.charcoal500} />
      <span>{T.translate('RECENTLY_VIEWED_SCHEDULE')}</span>
    </Box>
  );
};

function ScheduleDropdown({
  scheduleId,
  onChangeSchedule,
  schedules,
  recentlySeenSchedule,
}: {
  scheduleId: string;
  onChangeSchedule: (scheduleId: string) => void;
  schedules: Schedule[];
  recentlySeenSchedule: string | null;
}) {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));

  const scheduleOptions = useMemo(() => {
    const items = schedules.map((schedule: { name: string; id: string }) => ({
      value: schedule.id,
      label: schedule.name,
      subtext: recentlySeenSchedule === schedule.id ? <RecentlySeen /> : null,
    }));
    return [
      {
        label: T.translate('AVAILABLE_SCHEDULES'),
        items,
      },
    ];
  }, [T, schedules, recentlySeenSchedule]);

  const selectedSchedule = useMemo(() => {
    return scheduleOptions
      .flatMap((row) => row.items)
      .find((schedule) => schedule.value === scheduleId);
  }, [scheduleId, scheduleOptions]);

  const handleScheduleSelectChange = ({
    selectedItem = {},
  }: DropdownSingleSelectProps) => {
    const value = selectedItem?.value?.toString() || '';
    onChangeSchedule?.(value);
  };

  const renderMenu: DropdownRenderMenuType = (args) => {
    const { menuItems, getItemProps } = args;
    return (
      <Menu {...args}>
        {menuItems.map((itemOrGroupHeader, i) =>
          isGroupHeader(itemOrGroupHeader) ? (
            <MenuGroupHeader key={i}>{itemOrGroupHeader.label}</MenuGroupHeader>
          ) : (
            <MenuItem
              key={i}
              {...getItemProps({ item: itemOrGroupHeader })}
              text={
                <OverflowTooltip
                  content={defaultLabelGetter(itemOrGroupHeader)}
                  singleLine
                >
                  {defaultLabelGetter(itemOrGroupHeader)}
                </OverflowTooltip>
              }
            />
          ),
        )}
      </Menu>
    );
  };

  return (
    <div style={{ width: 345 }}>
      <Dropdown
        items={scheduleOptions}
        selectedItem={selectedSchedule}
        onSelectedItemChange={handleScheduleSelectChange}
        renderMenu={renderMenu}
      />
    </div>
  );
}

export default ScheduleDropdown;
